import { useState, useCallback, useEffect } from 'react';
import { ChatMessage, FinancialContext } from '../types/chat';
import { MockAdvisorBot } from '../services/mockAdvisorBot';

const bot = new MockAdvisorBot(); // This could be swapped with a real API implementation later

export const useAdvisorChat = (context: FinancialContext) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize chat with a welcome message
  useEffect(() => {
    const initializeChat = async () => {
      if (!isInitialized) {
        try {
          setIsLoading(true);
          const initialMessage = await bot.getInitialMessage(context);

          // Add bot initial message
          const botMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'assistant',
            content: initialMessage,
            timestamp: new Date(),
          };

          setMessages([botMessage]);
          setIsInitialized(true);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to initialize chat');
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeChat();
  }, [context, isInitialized]);

  const sendMessage = useCallback(
    async (content: string) => {
      try {
        setIsLoading(true);
        setError(null);

        // Add user message
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'user',
          content,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, userMessage]);

        // Get bot response
        const response = await bot.sendMessage(content, context);

        // Add bot response
        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, botMessage]);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to send message');
      } finally {
        setIsLoading(false);
      }
    },
    [context]
  );

  // Reset chat to initial state
  const resetChat = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setMessages([]);
      setIsInitialized(false);

      // Re-initialize with welcome message
      const initialMessage = await bot.getInitialMessage(context);

      // Add bot initial message
      const botMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: initialMessage,
        timestamp: new Date(),
      };

      setMessages([botMessage]);
      setIsInitialized(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset chat');
    } finally {
      setIsLoading(false);
    }
  }, [context]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    resetChat,
  };
};
